import { Box } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Fragment, ReactElement, ReactNode } from 'react';
import { tokens } from '@synergy/theme-palo';

/**
 * http(s) and www. addresses, without the punctuation that ends a sentence.
 * Nothing else is ever turned into a link: no javascript:, no mailto:.
 */
const LINK_PATTERN = /\b((?:https?:\/\/|www\.)[^\s<>"']*[^\s<>"'.,;:!?)\]])/gi;

const href = (raw: string): string => (/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);

/** Host and path, short enough for one line. */
export const paloLinkLabel = (raw: string): string => {
    try {
        const url = new URL(href(raw));

        return `${url.host}${url.pathname.replace(/\/$/, '')}`.slice(0, 70);
    } catch {
        return raw.slice(0, 70);
    }
};

/** The addresses in a text, each once, in the order written. */
export const paloFindLinks = (text: string | null | undefined): string[] =>
    Array.from(new Set((text ?? '').match(LINK_PATTERN) ?? []));

/** Opens in a new tab, so the document it sits in stays open with what is unsaved. */
export const PaloExternalLink = ({ url, children }: { url: string; children?: ReactNode }): ReactElement => (
    <Box
        component="a"
        href={href(url)}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ color: tokens.color.accentHi, textDecoration: 'underline', wordBreak: 'break-all' }}
    >
        {children ?? url}
    </Box>
);

/** A text as written, with every address in it clickable. */
export const PaloLinkifiedText = ({ text }: { text: string }): ReactElement => (
    <>
        {/* split() with a capturing group puts the matches at the odd places. */}
        {text
            .split(LINK_PATTERN)
            .map((part, i) =>
                1 === i % 2 ? <PaloExternalLink key={i} url={part} /> : <Fragment key={i}>{part}</Fragment>
            )}
    </>
);

/**
 * The addresses of a plain-text note, as links under its field. The note stays
 * plain text; the link saves marking, copying and pasting (R-261).
 */
export const PaloLinkRow = ({ text }: { text: string | null | undefined }): ReactElement | null => {
    const links = paloFindLinks(text);

    if (0 === links.length) {
        return null;
    }

    return (
        <Box
            sx={{
                mt: '4px',
                fontSize: 12,
                color: tokens.color.ink2,
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px 12px',
            }}
        >
            <span>
                <FormattedMessage id="app.common.links" />
            </span>
            {links.map(link => (
                <PaloExternalLink key={link} url={link}>
                    {paloLinkLabel(link)} ↗
                </PaloExternalLink>
            ))}
        </Box>
    );
};
